import { useMemo } from 'react';
import type { JSX, ReactNode } from 'react';
import type { AiChange, AiFocus, AiPersona, JournalEntry } from '@roll1/shared';
import { pluralize, useI18n } from '../../i18n';
import type { TranslationKeys } from '../../i18n';
import { useSessionContext } from '../../contexts/SessionContext';
import { useJournalContext } from '../../contexts/JournalContext';
import { useOptionalTokenContext } from '../../contexts/TokenContext';
import { useSheetContext } from '../../contexts/SheetContext';
import { useUserUiPrefs } from '../../hooks/useUserUiPrefs';
import { requestOpenRemarks } from '../../lib/aiBridge';
import type { AiChatBase, AiChatMessage } from '../../hooks/useAiChat';
import { TIA_EMOTION_URLS, getPersonaVisuals } from '../PersonaAvatar';
import TiaStage, { TIA_STAGE_SIZES } from './TiaStage';
import { ConversationHistory } from '../ai/ConversationHistory';
import { AiResultHistory } from '../ai/AiResultHistory';
import type { TiaStageSize, TiaStageState } from './TiaStage';

/**
 * The pieces around Tia's chat that are the same in the Studio column, the table chat and the player's chat
 * (group E): the stage with its state and size, the box «Allow changes», the line «Tia sees», the example chips.
 * `variant` is kept for the one behavioural difference that is left (the table chat's own select class); the surfaces and lines are the one shared palette now.
 */
export type TiaVariant = 'studio' | 'table';

const fill = (template: string, values: Record<string, string>): string =>
  Object.entries(values).reduce((text, [k, v]) => text.split(`{${k}}`).join(v), template);

// ── the stage ──

const isTiaPersona = (p: AiPersona | null | undefined): boolean => p === null || p === undefined || (p.type === 'builtin' && p.sourceId === 'tia');

/** How many changes the answer to the last request made (the ones not undone): «Done — N changes». */
function changesOfLastAnswer(messages: readonly AiChatMessage[]): number {
  let n = 0;
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i]!;
    if (m.role === 'user') break;
    if (m.role === 'changes' && m.turnData !== undefined) n += m.turnData.changes.filter(c => c.undone !== true && c.kept !== true).length;
  }
  return n;
}

export function TiaStageHost({ chat, persona, sizeKey, defaultSize, trailing }: {
  trailing?: ReactNode;
  chat: AiChatBase;
  persona: AiPersona | null | undefined;
  /** The user's own setting: the Studio column and the table / player chat keep their sizes apart. */
  sizeKey: 'tiaStageSize' | 'tiaStageSizeTable';
  defaultSize: TiaStageSize;
}): JSX.Element {
  const { t, locale } = useI18n();
  const { prefs, setPref } = useUserUiPrefs();
  const stored = prefs[sizeKey];
  const size: TiaStageSize = (TIA_STAGE_SIZES as readonly unknown[]).includes(stored) ? (stored as TiaStageSize) : defaultSize;
  const state: TiaStageState = chat.stageState ?? 'idle';
  const notes = chat.notesCount ?? 0;
  const tia = isTiaPersona(persona);
  const visuals = getPersonaVisuals(persona === null ? undefined : persona);
  const name = tia ? t('ai.title') : (visuals?.displayName ?? t('ai.title'));
  const emotion = chat.lastEmotion;
  const faceSrc = tia && emotion !== undefined && emotion !== 'neutral' ? TIA_EMOTION_URLS[emotion] : undefined;

  const statusText = state === 'notes'
    ? fill(locale === 'ru' ? pluralize(notes, t('tia.status.notes.one'), t('tia.status.notes.few'), t('tia.status.notes.many')) : (notes === 1 ? t('tia.status.notes.one') : t('tia.status.notes.few')), { n: String(notes) })
    : t(`tia.status.${state}` as keyof TranslationKeys);
  let say = '';
  if (state === 'thinking') say = t('tia.say.thinking');
  else if (state === 'done') {
    const n = changesOfLastAnswer(chat.messages);
    say = n > 0 ? fill(t('tia.say.doneN'), { n: String(n) }) : t('tia.say.done');
  } else if (state === 'error') say = chat.aiUnavailable === true ? t('tia.say.noKey') : t('tia.say.error');
  else if (state === 'notes') say = fill(t('tia.say.notes'), { n: String(notes) });

  return (
    <TiaStage
      state={state}
      size={size}
      onSizeChange={(next): void => setPref(sizeKey, next)}
      name={name}
      statusText={statusText}
      say={say}
      onSayClick={state === 'notes' ? (): void => { requestOpenRemarks(); } : undefined}
      portraitSrc={tia ? '/assets/tia-avatar.png' : visuals?.avatarUrl}
      portraitInitial={tia ? undefined : visuals?.initial}
      portraitColor={tia ? undefined : visuals?.color}
      faceSrc={faceSrc}
      trailing={trailing}
    />
  );
}

// ── the head bar ──

/**
 * ONE row over the chat (owner 31.10: «в несколько строк без всяких причин свалено ряд элементов»): the conversation
 * (picker, new), the history of changes, the surface's own action (the Studio's digest), the mode. Was four rows: the
 * mode box, the conversation bar, the history row, the «Tia sees» line (now a caption in the composer).
 */
export function TiaHeadBar({ chat, leading, onOpen, canOpen }: {
  chat: AiChatBase; leading?: ReactNode; onOpen?: (change: AiChange) => void; canOpen?: (change: AiChange) => boolean;
}): JSX.Element {
  const { t } = useI18n();
  return (
    <div className="flex shrink-0 items-center gap-0.5 border-b border-line px-2 py-1 group-has-[[data-size=perch]]/col:pt-14" data-testid="ai-chat-head">
      <ConversationHistory chat={chat} inline />
      <AiResultHistory chat={chat} onOpen={onOpen} canOpen={canOpen} compact />
      {leading}
      {chat.setAllowEdits !== undefined ? (
        <label title={t('ai.results.modeHint')} className="ai-mode-select ml-1 inline-flex shrink-0 items-center">
          <span className="sr-only">{t('ai.results.mode')}</span>
          <select
            className={`h-7 rounded border bg-panel px-1.5 text-meta text-fg ${chat.approvalMode === 'no-confirm' ? 'border-amber-500' : 'border-line'}`}
            value={chat.approvalMode ?? (chat.allowEdits ? 'auto' : 'suggest')}
            title={chat.approvalMode === 'no-confirm' ? t('ai.results.noConfirmWarning') : undefined}
            data-testid="ai-mode-select"
            onChange={event => {
              const next = event.target.value as 'suggest' | 'auto' | 'no-confirm';
              if (chat.setApprovalMode !== undefined) chat.setApprovalMode(next);
              else chat.setAllowEdits?.(next !== 'suggest');
            }}
          >
            <option value="suggest">{t('ai.results.suggest')}</option>
            <option value="auto">{t('ai.results.auto')}</option>
            {chat.setApprovalMode !== undefined && chat.noConfirmAllowed === true ? <option value="no-confirm">{t('ai.results.noConfirm')}</option> : null}
          </select>
        </label>
      ) : null}
      {chat.approvalMode === 'no-confirm' ? (
        <span className="ml-1 min-w-0 shrink truncate text-meta text-amber-600" data-testid="ai-no-confirm-warning">{t('ai.results.noConfirmWarning')}</span>
      ) : null}
    </div>
  );
}

// ── «Tia sees» ──

/** What Tia looks at, in words, from the very focus object that goes with the request. */
function useFocusParts(focus: AiFocus | undefined): { thing: string; rest: string[] } | null {
  const { t } = useI18n();
  const { mp, pages: pageStore } = useSessionContext();
  const journal = useJournalContext();
  const tokenCtx = useOptionalTokenContext();
  const liveTokens = tokenCtx?.tokens.tokens;
  const livePages = pageStore?.pages;
  const liveSheets = useSheetContext().sheets.sheets;
  const state = mp.serverState;
  const entries = journal.entries as readonly JournalEntry[];
  const parts = useMemo((): { thing: string; rest: string[] } | null => {
    if (focus === undefined || state === undefined || state === null) return null;
    const st = state;
    const beats = st.beats ?? [];
    const beatName = (id: string | null | undefined): string => {
      const b = id === null || id === undefined ? undefined : beats.find(x => x.id === id);
      return b === undefined ? '' : (b.sessionLabel ?? fill(t('ai.focus.beatN'), { n: String(b.order) }));
    };
    const currentId = st.currentBeatId ?? null;
    const currentName = beatName(currentId);
    const playing = currentName !== '' ? [fill(t('ai.focus.playing'), { name: currentName })] : [];
    if (focus.place === 'studio') {
      switch (focus.kind) {
        case 'beat': {
          const name = beatName(focus.id);
          const thing = fill(t('ai.focus.beat'), { name });
          return focus.id === currentId && currentId !== null ? { thing, rest: [t('ai.focus.playingNow')] } : { thing, rest: playing };
        }
        case 'journal': {
          const e = entries.find(x => x.id === focus.id);
          return { thing: fill(t(e?.type === 'quest' ? 'ai.focus.quest' : 'ai.focus.record'), { name: e?.name ?? '' }), rest: playing };
        }
        case 'page': {
          const p = livePages?.find(x => x.id === focus.id);
          return { thing: fill(t('ai.focus.map'), { name: p?.name ?? '' }), rest: playing };
        }
        case 'sheet': {
          const s = liveSheets?.find(x => x.id === focus.id);
          return { thing: fill(t('ai.focus.sheet'), { name: s?.name ?? '' }), rest: playing };
        }
        case 'remarks':
          return { thing: t('ai.focus.remarks'), rest: playing };
        default:
          return { thing: t('ai.focus.library'), rest: playing };
      }
    }
    const rest: string[] = [];
    if (currentName !== '') rest.push(fill(t('ai.focus.playing'), { name: currentName }));
    const page = (id: string | null): string => livePages?.find(p => p.id === id)?.name ?? '';
    if (focus.myPageId !== null && page(focus.myPageId) !== '') rest.push(fill(t('ai.focus.myMap'), { name: page(focus.myPageId) }));
    if (focus.playersPageId !== null && page(focus.playersPageId) !== '') rest.push(fill(t('ai.focus.playersMap'), { name: page(focus.playersPageId) }));
    if (focus.tokenIds.length > 0) {
      const names = focus.tokenIds.map(id => liveTokens?.find(tk => tk.id === id)?.name ?? '').filter(n => n !== '');
      if (names.length > 0) rest.push(fill(t('ai.focus.selected'), { names: names.join(', ') }));
    }
    return { thing: t('ai.focus.table'), rest };
  }, [focus, state, entries, t, livePages, liveTokens, liveSheets]);
  return parts;
}

/** What Tia sees: the caption at the top of the composer (it goes with the request typed under it). */
export function FocusLine({ focus }: { focus: AiFocus | undefined }): JSX.Element | null {
  const { t } = useI18n();
  const parts = useFocusParts(focus);
  if (parts === null) return null;
  return (
    <div
      data-testid="ai-focus-line"
      className="truncate px-2.5 pt-1.5 text-meta text-dim"
      title={[parts.thing, ...parts.rest].join(' · ')}
    >
      {t('ai.focus.prefix')} <b className="font-medium text-fg">{parts.thing}</b>
      {parts.rest.map((r, i) => (r.startsWith('(') ? <span key={i}> {r}</span> : <span key={i}> · {r}</span>))}
    </div>
  );
}

// ── chips ──

/** The example requests under the box, by what Tia looks at. */
export function chipsFor(focus: AiFocus | undefined, state: { entries: readonly JournalEntry[]; sheetType: (id: string) => string | undefined }, t: (key: keyof TranslationKeys) => string): string[] {
  if (focus === undefined) return [];
  if (focus.place === 'table') {
    const chips = [t('ai.chip.table1'), t('ai.chip.table2'), t('ai.chip.table3')];
    if (focus.tokenIds.length > 0) chips.push(t('ai.chip.npc2'));
    return chips;
  }
  switch (focus.kind) {
    case 'beat': return [t('ai.chip.beat1'), t('ai.chip.beat2'), t('ai.chip.beat3')];
    case 'journal': {
      const e = state.entries.find(x => x.id === focus.id);
      return e?.type === 'quest' ? [t('ai.chip.quest1'), t('ai.chip.quest2')] : [t('ai.chip.lib1'), t('ai.chip.lib2')];
    }
    case 'sheet': return focus.id !== undefined && state.sheetType(focus.id) === 'npc' ? [t('ai.chip.npc1'), t('ai.chip.npc2')] : [t('ai.chip.lib1'), t('ai.chip.lib2')];
    default: return [t('ai.chip.lib1'), t('ai.chip.lib2')];
  }
}


export function latestSuggestedReplies(messages: readonly AiChatMessage[], focus?: AiFocus): string[] {
  const request = [...messages].reverse().find(m => m.role === 'user');
  if (request?.focus && JSON.stringify(request.focus) !== JSON.stringify(focus)) return [];
  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i]!;
    if (message.role === 'user' || message.role === 'error') break;
    if (message.role === 'assistant') return message.suggestedReplies ?? [];
  }
  return [];
}

export function Chips({ focus, onPick, variant, empty = true, messages = [], disabled = false }: { empty?: boolean; messages?: readonly AiChatMessage[]; disabled?: boolean; focus: AiFocus | undefined; onPick: (text: string) => void; variant: TiaVariant }): JSX.Element | null {
  const { t } = useI18n();
  const journal = useJournalContext();
  const entries = journal.entries as readonly JournalEntry[];
  const sheets = useSheetContext().sheets.sheets;
  const defaults = useMemo(
    () => chipsFor(focus, { entries, sheetType: (id) => sheets?.find(s => s.id === id)?.sheetType }, t),
    [focus, entries, sheets, t],
  );
  const texts = empty ? defaults : latestSuggestedReplies(messages, focus);
  if (texts.length === 0) return null;
  return (
    <div className="flex shrink-0 flex-wrap gap-1.5 px-3 pb-2 pt-2">
      {texts.map((text) => (
        <button
          key={text}
          type="button"
          data-testid="ai-chip"
          disabled={disabled}
          onClick={(): void => onPick(text)}
          className={`rounded-full border px-2.5 py-0.5 text-left text-meta border-line text-muted-foreground hover:text-fg hover:border-ai disabled:opacity-40 disabled:cursor-default`}
        >
          {text}
        </button>
      ))}
    </div>
  );
}

/** The small caption over a request in the chat: what Tia was looking at when it was sent («the table», «beat “…”»). */
export function FocusCaption({ focus, className }: { focus: AiFocus | undefined; className?: string }): JSX.Element | null {
  const parts = useFocusParts(focus);
  if (parts === null) return null;
  return <div className={className ?? 'text-pill text-ai/80'} data-testid="ai-request-context">{parts.thing}</div>;
}
